// CANONICAL LANDING DESIGN — DO NOT EDIT MARKUP OR CLASSES.
// Only allowed changes: handler wiring and import paths.

import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTrack } from "../contexts/TrackContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRightIcon,
  CheckBadgeIcon,
  ChartBarIcon,
  BoltIcon,
  CheckIcon,
  LockClosedIcon,
  PlayIcon,
  ShieldCheckIcon,
  ClockIcon,
  SparklesIcon,
  StarIcon,
} from "@heroicons/react/24/outline";
import FaqSection from "../components/landing/FaqSection";
import { LoadingSpinner } from "../components/LoadingSpinner";
import LaunchAnnouncementBanner from "../components/LaunchAnnouncementBanner";
import { PRODUCTION_ORIGIN } from "../config/siteUrl";
import { BILLING_UI_TO_PAYFAST, PRICING_PLANS } from "../config/pricingLadder";
import { useSubscription } from "../hooks/useSubscription";
import useAuthUser from "../services/useAuthUser";
import { auth } from "../services/firebase";

const API_BASE = process.env.REACT_APP_API_URL || "";
const PROMO_POLL_MS = 30000;
const MCK_PROMO_SLUG = "mck_assessment_beta_26";
const BCG_PROMO_SLUG = "bcg_casey_beta_26";

type PromoSlug = typeof MCK_PROMO_SLUG | typeof BCG_PROMO_SLUG;
type PromoCampaignStatus = {
  status?: string;
  remaining_claims?: number;
};

/**
 * Cognidoor Landing (V4 Signature)
 *
 * ✅ Single design: V4 Signature
 * ✅ Global toggles: Track / Billing
 * ✅ Pricing updated per launch_v1_final_v2_monthly_premium
 * ✅ Track toggle affects recommended plans
 * ✅ Demo iframe placeholders included:
 *    - Track=All: Sea‑Wolf + Casey demos shown
 *    - Track=McKinsey: Sea-Wolf demo shown
 *    - Track=BCG: Casey demo shown
 * ✅ Brand blues pulled from Cognidoor infinity mark (approx):
 *    - --cog-blue-1: #277AB2
 *    - --cog-blue-2: #0B5996
 *    - --cog-blue-3: #082B4A
 */

const cx = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(" ");

/**
 * Demo URL resolution policy:
 * - explicit env override always wins
 * - localhost uses direct dev-server origins by design
 * - deployed hosts use same-origin subpaths
 */
const resolveDemoUrl = (envValue: string | undefined, localUrl: string, subpathUrl: string) => {
  if (envValue) return envValue;
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") {
      return localUrl;
    }
  }
  return subpathUrl;
};

/**
 * Red Rock iframe policy:
 * - explicit env override always wins
 * - otherwise prefer the same-origin `/redrock-app` mount so the embed works
 *   through the CRA dev proxy and avoids mixed-content issues when the parent
 *   page is loaded over HTTPS
 */
const resolveRedRockDemoBase = (envValue: string | undefined) => envValue || "/redrock-app";

const SEA_WOLF_DEMO_URL = resolveDemoUrl(
  process.env.REACT_APP_SEAWOLF_DEMO_URL,
  "http://localhost:8080/sea-wolf?demo=1",
  "/seawolf-simulator/sea-wolf?demo=1"
);
const CASEY_DEMO_URL = resolveDemoUrl(
  process.env.REACT_APP_CASEY_DEMO_URL,
  "http://localhost:5181/demo?demo=1",
  "/casey/demo?demo=1"
);
// Coach demos: localhost uses direct dev ports; deployed/production use app mounts (not marketing routes).
const MCKINSEY_AI_COACH_DEMO_URL = resolveDemoUrl(
  process.env.REACT_APP_MCKINSEY_AI_COACH_DEMO_URL,
  "http://localhost:5174",
  "/apps/mckinsey-ai-coach"
);
const BCG_AI_COACH_DEMO_URL = resolveDemoUrl(
  process.env.REACT_APP_BCG_AI_COACH_DEMO_URL,
  "http://localhost:5176",
  "/apps/bcg-ai-coach"
);
const REDROCK_DEMO_BASE = resolveRedRockDemoBase(process.env.REACT_APP_REDROCK_APP_URL);
// Embedded demo: coach mode only, capped at second exhibit
const REDROCK_DEMO_URL = `${REDROCK_DEMO_BASE.replace(/\/$/, "")}/play/stratosat?mode=coach&demo=1&maxExhibit=2`;

/**
 * True when the app is running on a Vercel preview deployment (*.vercel.app).
 * On preview, demo iframes load from production origin since the preview build does not serve sub-app routes.
 */
function isVercelPreview(): boolean {
  if (typeof window === "undefined") return false;
  return window.location.hostname.toLowerCase().endsWith(".vercel.app");
}

/**
 * True when demo iframes can load: localhost (dev proxy), production domain, or Vercel preview.
 * On Vercel preview we load iframes from production origin (see getEffectiveDemoIframeUrl).
 */
function isDemosEnvironmentAvailable(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname.toLowerCase();
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "www.cognidoor.com" ||
    host === "cognidoor.com" ||
    host.endsWith(".vercel.app")
  );
}

/** Subpaths used for demos on production (and for iframe src when on Vercel preview). */
const DEMO_SUBPATHS = {
  seaWolf: "/seawolf-simulator/sea-wolf?demo=1",
  casey: "/casey/demo?demo=1",
  redrock: "/redrock-app/play/stratosat?mode=coach&demo=1&maxExhibit=2",
  mckinseyCoach: "/apps/mckinsey-ai-coach?demo=1",
  bcgCoach: "/apps/bcg-ai-coach?demo=1",
} as const;

/**
 * When on Vercel preview, demo iframes must load from production (preview does not serve sub-app routes).
 * Otherwise use the given url (localhost or same-origin subpath).
 */
function getEffectiveDemoIframeUrl(subpathKey: keyof typeof DEMO_SUBPATHS, currentUrl: string): string {
  if (typeof window === "undefined") return currentUrl;
  if (isVercelPreview()) return `${PRODUCTION_ORIGIN.replace(/\/$/, "")}${DEMO_SUBPATHS[subpathKey]}`;
  return currentUrl;
}

type Track = "all" | "mck" | "bcg";

/** Per-slot demo variant: assessment (Sea-Wolf / Casey), AI coach, or Red Rock */
type DemoVariant = "assessment" | "aiCoach" | "redrock";

type Billing = "monthly" | "sixMo";

type Tone = "light" | "dark";

type Plan = {
  plan_id:
    | "solve_assessment"
    | "casey_assessment"
    | "assessments_bundle"
    | "mckinsey_ai_coach_only"
    | "bcg_ai_coach_only"
    | "mckinsey_track_full"
    | "bcg_track_full"
    | "all_access";
  name: string;
  product_family: "mckinsey" | "bcg" | "bundle";
  includes: string[];
  monthly: number;
  sixMo: number;
  tag?: string;
  bestFor?: string;
};

const PLAN_INCLUDES: Record<string, string[]> = {
  solve_assessment: ["Sea-Wolf solver", "Sea-Wolf simulation", "Red Rock Simulation"],
  casey_assessment: ["BCG Casey simulation"],
  assessments_bundle: ["Sea-Wolf Assessment", "Casey Assessment", "Red Rock Simulation"],
  mckinsey_ai_coach_only: ["McKinsey Lilli AI case Coach"],
  bcg_ai_coach_only: ["BCG Candidate-led AI case Coach"],
  mckinsey_track_full: ["Sea-Wolf solver", "Sea-Wolf simulation", "McKinsey Lilli AI case Coach", "Red Rock Simulation"],
  bcg_track_full: ["BCG Casey simulation", "BCG Candidate-led AI case Coach"],
  all_access: ["McKinsey Track (Full)", "BCG Track (Full)", "Sea-Wolf Assessment", "Casey Assessment", "Red Rock Simulation"],
};
const PLAN_TAGS: Record<string, string> = {
  solve_assessment: "Digital assessments only",
  casey_assessment: "Digital assessments only",
  assessments_bundle: "Digital assessments only",
  mckinsey_ai_coach_only: "AI coach only",
  bcg_ai_coach_only: "AI coach only",
  mckinsey_track_full: "Most popular",
  bcg_track_full: "Most popular",
  all_access: "Everything included",
};

/** Pricing from canonical ladder (single source of truth for plan_id and amounts). */
const PRICING: Plan[] = PRICING_PLANS.map((p) => ({
  plan_id: p.plan_id,
  name: p.name,
  product_family: p.product_family,
  includes: PLAN_INCLUDES[p.plan_id] ?? [],
  monthly: p.monthly,
  sixMo: p.six_month,
  tag: PLAN_TAGS[p.plan_id],
}));

function getRecommendedPlans(track: Track): Plan[] {
  // Intent: simple + relevant.
  if (track === "mck") {
    return PRICING.filter((p) =>
      ["solve_assessment", "mckinsey_ai_coach_only", "mckinsey_track_full", "all_access"].includes(p.plan_id)
    );
  }
  if (track === "bcg") {
    return PRICING.filter((p) =>
      ["casey_assessment", "bcg_ai_coach_only", "bcg_track_full", "all_access"].includes(p.plan_id)
    );
  }
  // all
  return PRICING.filter((p) =>
    [
      "assessments_bundle",
      "mckinsey_track_full",
      "bcg_track_full",
      "all_access",
    ].includes(p.plan_id)
  );
}

// ---------- Shared UI bits ----------

function Segmented({
  value,
  onChange,
  items,
  tone = "light",
  indicatorId = "segmented",
  appearance = "default",
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  items: Array<{ value: string; label: string }>;
  tone?: Tone;
  indicatorId?: string;
  appearance?: "default" | "softPill";
  className?: string;
}) {
  const isSoftPill = appearance === "softPill";
  const wrap =
    tone === "dark"
      ? "border-white/10 bg-white/5"
      : isSoftPill
      ? "inline-flex gap-2 items-center px-4 py-1.5 rounded-full text-sm bg-white border-2 border-blue-400 shadow-inner"
      : "border-slate-200 bg-slate-50";
  const idle =
    tone === "dark"
      ? "text-white/70 hover:bg-white/10"
      : isSoftPill
      ? "text-gray-500"
      : "text-slate-600 hover:bg-white/60";
  const activeText =
    tone === "dark"
      ? "text-slate-950"
      : isSoftPill
      ? "text-blue-600 font-semibold bg-blue-50 shadow-sm"
      : "text-white";
  const pillRound = isSoftPill ? "" : "rounded-2xl";
  const segmentRound = isSoftPill ? "rounded-full" : "rounded-xl";
  const padding = isSoftPill ? "" : "p-1";
  const segmentPadding = isSoftPill ? "px-3 py-1" : "px-4 py-2";

  return (
    <div className={cx("relative", isSoftPill ? wrap : null, !isSoftPill && "inline-flex border", !isSoftPill && pillRound, !isSoftPill && padding, !isSoftPill && wrap, className)}>
      {items.map((it) => {
        const isActive = it.value === value;
        return (
          <button
            key={it.value}
            onClick={() => onChange(it.value)}
            className={cx(
              "text-sm transition-all duration-300",
              !isSoftPill && "relative",
              segmentRound,
              segmentPadding,
              isActive ? activeText : idle
            )}
          >
            {!isSoftPill && isActive ? (
              <motion.div
                layoutId={indicatorId}
                className={cx(
                  "absolute inset-0",
                  segmentRound,
                  tone === "dark"
                    ? "bg-white"
                    : "bg-gradient-to-r from-blue-600 to-blue-400 shadow-md"
                )}
                transition={{ type: "tween", duration: 0.16, ease: [0.4, 0, 0.2, 1] }}
              />
            ) : null}
            {!isSoftPill ? <span className="relative z-10">{it.label}</span> : it.label}
          </button>
        );
      })}
    </div>
  );
}

function Button({
  children,
  variant = "primary",
  className,
  onClick,
}: {
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "ghost" | "dark";
  className?: string;
  onClick?: () => void;
}) {
  const base =
    "inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold transition-all duration-300 active:scale-[0.99]";

  const primary =
    "rounded-xl text-white shadow-md hover:shadow-xl transform hover:scale-105 bg-gradient-to-r from-blue-600 to-blue-400";

  const styles =
    variant === "primary"
      ? primary
      :     variant === "secondary"
      ? "rounded-2xl bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 shadow-sm"
      :     variant === "dark"
      ? "rounded-2xl bg-white/10 text-white border border-white/15 hover:bg-white/15"
      : "rounded-2xl bg-transparent text-slate-700 hover:bg-slate-100";

  return (
    <button onClick={onClick} className={cx(base, styles, className)}>
      {children}
    </button>
  );
}

function Glass({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cx(
        "rounded-3xl border border-white/15 bg-white/12 shadow-[0_10px_40px_-20px_rgba(2,6,23,0.45)] backdrop-blur",
        className
      )}
    >
      {children}
    </div>
  );
}

function PlanTag({ tag, accent }: { tag: string; accent: Tone }) {
  const isPopular = /popular|most popular/i.test(tag);
  const isEverything = /everything/i.test(tag);
  const Icon = isPopular ? StarIcon : isEverything ? SparklesIcon : CheckBadgeIcon;
  const lightBase =
    "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold shadow-sm ring-1 whitespace-nowrap text-white";
  const lightClass = isPopular
    ? cx(lightBase, "ring-emerald-200/60 bg-gradient-to-r from-emerald-600 to-emerald-500")
    : cx(lightBase, "ring-blue-200/60 bg-gradient-to-r from-blue-600 to-blue-500");
  const darkBase =
    "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold whitespace-nowrap text-white";
  const darkClass = isPopular
    ? cx(darkBase, "bg-emerald-500/25 ring-emerald-400/30")
    : cx(darkBase, "bg-white/15 ring-1 ring-white/20");
  return (
    <span
      className={cx(
        accent === "dark" ? darkClass : lightClass,
        isPopular && "origin-center scale-[1.01]"
      )}
    >
      <Icon className="w-4 h-4" aria-hidden />
      {tag}
    </span>
  );
}

function PlanCard({
  plan,
  billing,
  accent = "light",
  onGetStarted,
  onTryDemo,
  tagOverride,
  isCurrentPlan = false,
  isDimmed = false,
  showUpgrade = false,
  isHighlighted = false,
}: {
  plan: Plan;
  billing: Billing;
  accent?: Tone;
  onGetStarted?: (plan: Plan, billing: Billing) => void;
  onTryDemo?: () => void;
  /** Override plan.tag (e.g. track-specific tag). */
  tagOverride?: string;
  /** User's active plan — show "Your current plan" and non-actionable primary. */
  isCurrentPlan?: boolean;
  /** When user has all_access, other plans are dimmed (included in plan). */
  isDimmed?: boolean;
  /** Show "Upgrade" instead of "Get started" (logged in with a different plan). */
  showUpgrade?: boolean;
  /** Highlight card when navigating from product gate (e.g. coach upgrade CTA). */
  isHighlighted?: boolean;
}) {
  const price = billing === "monthly" ? plan.monthly : plan.sixMo;
  const suffix = billing === "monthly" ? "/mo" : "/6 mo";
  const effectiveTag = tagOverride ?? plan.tag;
  const isPremiumPlan = /popular|everything/i.test(effectiveTag ?? "");

  return (
    <div
      className={cx(
        "group relative w-full min-w-0 rounded-xl border-2 p-6 flex flex-col h-full min-h-[380px] transition-all duration-500 ease-out overflow-visible",
        isHighlighted && !isDimmed && "ring-2 ring-sky-500",
        isDimmed && "opacity-60 pointer-events-none select-none",
        !isDimmed &&
          (accent === "dark"
            ? cx(
                "bg-white/5 text-white hover:border-white/20 hover:bg-white/[0.08]",
                isPremiumPlan
                  ? "border-white/20 hover:-translate-y-1.5 hover:shadow-[0_28px_72px_-30px_rgba(125,211,252,0.28)]"
                  : "border-white/10 hover:shadow-[0_24px_60px_-32px_rgba(255,255,255,0.18)]"
              )
            : cx(
                "bg-white shadow-md",
                isPremiumPlan
                  ? "border-sky-500 hover:-translate-y-1.5 hover:shadow-[0_30px_72px_-28px_rgba(14,165,233,0.28)]"
                  : "border-blue-400 hover:-translate-y-1 hover:shadow-[0_24px_60px_-28px_rgba(11,89,150,0.22)]"
              ))
      )}
    >
      <div className="pointer-events-none absolute inset-0 rounded-[inherit] overflow-hidden opacity-0 transition-opacity duration-500 group-hover:opacity-100">
        <div
          className={cx(
            "absolute -right-16 -top-16 h-40 w-40 rounded-full blur-3xl",
            accent === "dark"
              ? isPremiumPlan
                ? "bg-gradient-to-br from-sky-200/30 via-cyan-300/20 to-transparent opacity-80"
                : "bg-gradient-to-br from-white/20 via-sky-300/10 to-transparent opacity-60"
              : isPremiumPlan
                ? "bg-gradient-to-br from-cyan-300/35 via-sky-300/25 to-transparent opacity-100"
                : "bg-gradient-to-br from-sky-400/25 via-blue-300/20 to-transparent opacity-100"
          )}
        />
        <div
          className={cx(
            "absolute inset-x-0 top-0 h-px",
            accent === "dark"
              ? isPremiumPlan
                ? "bg-gradient-to-r from-transparent via-sky-200/90 to-transparent"
                : "bg-gradient-to-r from-transparent via-white/70 to-transparent"
              : isPremiumPlan
                ? "bg-gradient-to-r from-transparent via-cyan-300 to-transparent"
                : "bg-gradient-to-r from-transparent via-sky-300/90 to-transparent"
          )}
        />
      </div>
      {/* Tag on top border center, straddling the edge */}
      {effectiveTag ? (
        <div
          className={cx(
            "absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 z-10 transition-transform duration-500",
            isPremiumPlan ? "group-hover:-translate-y-[60%]" : "group-hover:-translate-y-[55%]"
          )}
        >
          <PlanTag tag={effectiveTag} accent={accent} />
        </div>
      ) : null}
      {/* Header: title centered; extra pt when tag exists so content clears the tag */}
      <div
        className={cx(
          "flex flex-col items-center text-center flex-shrink-0 min-h-[56px]",
          effectiveTag ? "pt-5" : ""
        )}
      >
        <div
          className={cx(
            "text-xl font-extrabold tracking-tight transition-colors duration-300",
            accent === "dark" ? "text-white" : "text-slate-900 group-hover:text-slate-950"
          )}
        >
          {plan.name}
        </div>
        {plan.bestFor ? (
          <div
            className={cx(
              "mt-1 text-sm",
              accent === "dark" ? "text-white/70" : "text-slate-600"
            )}
          >
            {plan.bestFor}
          </div>
        ) : null}
      </div>

      {/* Price row: fixed min-height; price slides vertically when billing toggles; centered in card */}
      <div className="mt-5 flex items-baseline justify-center gap-2 flex-shrink-0 min-h-[44px] overflow-hidden transition-transform duration-500 group-hover:translate-x-0.5">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={billing}
            initial={{ y: 14, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -14, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.25, 0.1, 0.25, 1] }}
            className="flex items-baseline gap-2"
          >
            <div
              className={cx(
                "text-3xl font-extrabold",
                accent === "dark" ? "text-white" : "text-slate-900"
              )}
            >
              ${price.toFixed(2)}
            </div>
            <div
              className={cx(
                "text-sm font-bold",
                accent === "dark" ? "text-white/60" : "text-slate-500"
              )}
            >
              {suffix}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Includes: flex-1 so buttons stay at bottom; same top position across cards; left-aligned */}
      <div className="mt-5 space-y-2 flex-1 min-h-0 text-left">
        {plan.includes.map((h) => (
          <div
            key={h}
            className={cx(
              "flex items-start gap-2 text-sm transition-transform duration-300 group-hover:translate-x-0.5",
              accent === "dark" ? "text-white/85" : "text-slate-700"
            )}
          >
            <CheckIcon
              className={cx(
                "w-4 h-4 mt-0.5 flex-shrink-0 transition-transform duration-300 group-hover:scale-110",
                accent === "dark" ? "text-white/85" : "text-sky-600"
              )}
              aria-hidden
            />
            <span className="flex-1">{h}</span>
          </div>
        ))}
      </div>

      <div className="mt-auto pt-6 grid gap-2 flex-shrink-0">
        {isCurrentPlan ? (
          <div
            className={cx(
              "w-full flex items-center justify-center gap-2 rounded-xl border-2 py-3 px-4 text-sm font-semibold",
              accent === "dark"
                ? "border-white/30 bg-white/10 text-white"
                : "border-slate-300 bg-slate-100 text-slate-700"
            )}
          >
            Your current plan
          </div>
        ) : isDimmed ? (
          <div
            className={cx(
              "w-full flex items-center justify-center rounded-xl border border-dashed py-3 px-4 text-sm font-medium",
              accent === "dark" ? "border-white/20 text-white/60" : "border-slate-300 text-slate-500"
            )}
          >
            Included in All-Access
          </div>
        ) : (
          <Button
            className={cx(
              "w-full transition-transform duration-300 group-hover:translate-y-0",
              accent === "dark" ? "bg-white text-slate-950 hover:bg-white/90" : ""
            )}
            onClick={() => onGetStarted?.(plan, billing)}
          >
            {showUpgrade ? "Upgrade" : "Get started"} <ArrowRightIcon className="w-4 h-4" />
          </Button>
        )}
        {!isDimmed && (
          <Button
            variant={accent === "dark" ? "dark" : "secondary"}
            className="w-full"
            onClick={onTryDemo}
          >
            Try demo first
          </Button>
        )}
      </div>
    </div>
  );
}

// ---------- Demo section (iframe placeholders) ----------

function useIframeResize(iframeRef: React.RefObject<HTMLIFrameElement | null>, sourceTag: string) {
  useEffect(() => {
    let lastHeight = 0;
    function onMessage(e: MessageEvent) {
      if (!e.data || e.data.type !== "COGNIDOOR_IFRAME_HEIGHT") return;
      if (e.data.source !== sourceTag) return;
      if (!iframeRef.current) return;
      const rawHeight = Number(e.data.height);
      if (!Number.isFinite(rawHeight)) return;
      const clamped = Math.max(360, Math.min(900, Math.round(rawHeight)));
      if (Math.abs(clamped - lastHeight) < 2) return;
      if (iframeRef.current.style.height === `${clamped}px`) return;
      lastHeight = clamped;
      iframeRef.current.style.height = `${clamped}px`;
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [iframeRef, sourceTag]);
}

const DEMOS_UNAVAILABLE_MESSAGE =
  "Demos aren't available in this preview. They run on production or when you run the app locally with the simulator and coach apps.";

function DemoFrame({
  title,
  subtitle,
  tone,
  iframeHint,
  iframeSrc,
  iframeSourceTag,
  iframeScale,
  iframeHeight,
  iframeMaxHeight,
  iframeMinHeight,
  bareIframe,
  expandLabel,
  onExpand,
  matchColumnHeight,
  hideScrollbar,
  /** When true, do not load iframe; show placeholder (e.g. on Vercel preview where subpaths 404). */
  demosUnavailable,
  /** When set, shown below iframe when it fails to load (e.g. coach app not running). */
  iframeLoadFallbackHint,
  /** When set, always shown as a small hint below the iframe (e.g. how to run the demo locally). */
  iframeRunHint,
}: {
  title: string;
  subtitle: string;
  tone: Tone;
  iframeHint: string;
  iframeSrc: string;
  iframeSourceTag: string;
  iframeScale?: number;
  iframeHeight?: number;
  /** Max height for the iframe (e.g. "58vh") when bareIframe — makes the game area slightly shorter. */
  iframeMaxHeight?: string;
  /** Min height for the iframe (e.g. "50vh") when bareIframe — ensures embed has visible area. */
  iframeMinHeight?: string;
  /** When true, iframe is shown without the inner white rounded box (Sea-Wolf only). */
  bareIframe?: boolean;
  expandLabel?: string;
  onExpand?: () => void;
  /** When true, card uses min-h-[70vh] and flex so it aligns with sibling demo cards (e.g. two-column layout). */
  matchColumnHeight?: boolean;
  /** When true, hide scrollbar on the iframe wrapper (e.g. Sea Wolf demo in All track). */
  hideScrollbar?: boolean;
  demosUnavailable?: boolean;
  iframeLoadFallbackHint?: string;
  iframeRunHint?: string;
}) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [iframeLoadFailed, setIframeLoadFailed] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(true);
  useIframeResize(iframeRef, iframeSourceTag);

  const showPlaceholder = demosUnavailable || (iframeLoadFailed && iframeLoadFallbackHint);
  const placeholderMessage = demosUnavailable ? DEMOS_UNAVAILABLE_MESSAGE : (iframeLoadFallbackHint ?? "");

  const onIframeLoad = () => {
    setIframeLoading(false);
  };

  const shell =
    tone === "dark"
      ? "border-white/10 bg-white/5 text-white"
      : "border-slate-200 bg-white text-slate-900";
  const sub = tone === "dark" ? "text-white/70" : "text-slate-600";
  const pill =
    tone === "dark"
      ? "border-white/15 bg-white/10 text-white/85"
      : "border-slate-200 bg-slate-50 text-slate-700";

  const useMinHeight = bareIframe || matchColumnHeight;

  return (
    <div
      className={cx(
        "rounded-3xl border overflow-hidden",
        shell,
        useMinHeight && "flex flex-col min-h-[70vh]",
        matchColumnHeight && "flex-1 min-h-0"
      )}
    >
      <div className={cx("p-6 border-b shrink-0 text-left", tone === "dark" ? "border-white/10" : "border-slate-200")}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-[11rem]">
            <div className="text-sm font-extrabold">{title}</div>
            <div className={cx("mt-1 text-sm", sub)}>{subtitle}</div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className={cx("rounded-full border px-3 py-1 text-xs font-semibold", pill)}>
              <LockClosedIcon className="w-3.5 h-3.5 mr-2 inline" /> Free preview
            </span>
            <span className={cx("rounded-full border px-3 py-1 text-xs font-semibold", pill)}>
              <ShieldCheckIcon className="w-3.5 h-3.5 mr-2 inline" /> Read-only
            </span>
            {expandLabel && onExpand && (
              <button
                type="button"
                onClick={onExpand}
                className={cx(
                  "rounded-xl border-2 border-blue-400 bg-blue-50 px-4 py-2 text-xs font-semibold text-blue-600 shadow-md transition hover:scale-[1.02] hover:shadow-lg",
                  "cursor-pointer"
                )}
                aria-label={`${expandLabel} — view full demo`}
              >
                View full demo
              </button>
            )}
          </div>
        </div>
      </div>

      <div
        className={cx(
          "py-6",
          bareIframe ? "px-0 flex-1 min-h-0 flex flex-col" : "px-8 md:px-12 lg:px-16",
          matchColumnHeight && !bareIframe && "flex-1 min-h-0 flex flex-col",
          tone === "dark" ? "bg-slate-950/20" : "bg-slate-50"
        )}
      >
        {bareIframe ? (
          <div
            className={cx(
              "h-full min-h-0 w-full flex flex-col overflow-hidden",
              hideScrollbar && "[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            )}
            style={{
              ...(iframeMaxHeight ? { maxHeight: iframeMaxHeight } : {}),
              ...(iframeMinHeight ? { minHeight: iframeMinHeight } : {}),
            }}
          >
            {showPlaceholder ? (
              <div
                className={cx(
                  "flex-1 min-h-[280px] flex flex-col items-center justify-center p-6 rounded-2xl border text-center",
                  tone === "dark" ? "bg-slate-900/40 border-white/10 text-white/80" : "bg-slate-100 border-slate-200 text-slate-600"
                )}
              >
                <p className="text-sm font-medium mb-2">
                  {demosUnavailable ? "Preview mode" : "Demo could not load."}
                </p>
                <p className="text-xs max-w-md mb-3">{placeholderMessage}</p>
                {demosUnavailable && (
                  <a
                    href="https://www.cognidoor.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cx(
                      "text-sm font-semibold underline underline-offset-2",
                      tone === "dark" ? "text-sky-300 hover:text-sky-200" : "text-blue-600 hover:text-blue-700"
                    )}
                  >
                    View demos on cognidoor.com →
                  </a>
                )}
              </div>
            ) : (
              <>
                <div className="relative h-full min-h-0 flex-1 flex flex-col">
                  {iframeLoading && (
                    <div
                      className={cx(
                        "absolute inset-0 z-10 flex flex-col items-center justify-center rounded-2xl",
                        tone === "dark" ? "bg-slate-900/80" : "bg-slate-50/95"
                      )}
                      aria-hidden
                    >
                      <LoadingSpinner tone={tone === "dark" ? "dark" : "light"} size="md" />
                      <p className={cx("mt-3 text-xs font-medium", tone === "dark" ? "text-slate-400" : "text-slate-500")}>
                        Loading demo…
                      </p>
                    </div>
                  )}
                  {iframeScale != null && iframeScale > 0 && iframeScale < 1 ? (
                    <div className="h-full w-full overflow-hidden rounded-2xl">
                      <div
                        style={{
                          transform: `scale(${iframeScale})`,
                          transformOrigin: "top center",
                          width: "100%",
                          height: `${100 / iframeScale}%`,
                        }}
                        className="rounded-2xl overflow-hidden"
                      >
                        <iframe
                          ref={iframeRef}
                          src={iframeSrc}
                          className="w-full h-full min-h-0 border-0"
                          title={title}
                          loading="lazy"
                          allow="fullscreen; clipboard-read; clipboard-write"
                          referrerPolicy="no-referrer-when-downgrade"
                          onLoad={onIframeLoad}
                          onError={() => { setIframeLoadFailed(true); setIframeLoading(false); }}
                        />
                      </div>
                    </div>
                  ) : (
                    <iframe
                      ref={iframeRef}
                      src={iframeSrc}
                      className="w-full flex-1 min-h-0 border-0 rounded-2xl overflow-hidden"
                      title={title}
                      loading="lazy"
                      allow="fullscreen; clipboard-read; clipboard-write"
                      referrerPolicy="no-referrer-when-downgrade"
                      onLoad={onIframeLoad}
                      onError={() => { setIframeLoadFailed(true); setIframeLoading(false); }}
                    />
                  )}
                </div>
              </>
            )}
            {iframeRunHint ? (
              <p className={cx("mt-2 px-2 text-[11px] text-center", tone === "dark" ? "text-white/50" : "text-slate-500")}>
                {iframeRunHint}
              </p>
            ) : null}
          </div>
        ) : showPlaceholder ? (
          <div
            className={cx(
              "flex-1 min-h-[280px] flex flex-col items-center justify-center p-6 rounded-3xl border text-center",
              tone === "dark" ? "bg-slate-900/40 border-white/10 text-white/80" : "bg-slate-100 border-slate-200 text-slate-600"
            )}
          >
            <p className="text-sm font-medium mb-2">
              {demosUnavailable ? "Preview mode" : "Demo could not load."}
            </p>
            <p className="text-xs max-w-md mb-3">{placeholderMessage}</p>
            {demosUnavailable && (
              <a
                href="https://www.cognidoor.com"
                target="_blank"
                rel="noopener noreferrer"
                className={cx(
                  "text-sm font-semibold underline underline-offset-2",
                  tone === "dark" ? "text-sky-300 hover:text-sky-200" : "text-blue-600 hover:text-blue-700"
                )}
              >
                View demos on cognidoor.com →
              </a>
            )}
          </div>
        ) : (
          <div
            className={cx(
              "rounded-3xl border overflow-hidden relative",
              tone === "dark" ? "border-white/10 bg-black/20" : "border-slate-200 bg-white",
              matchColumnHeight && "flex-1 min-h-0 flex flex-col"
            )}
          >
            {iframeLoading && (
              <div
                className={cx(
                  "absolute inset-0 z-10 flex flex-col items-center justify-center rounded-3xl",
                  tone === "dark" ? "bg-slate-900/80" : "bg-slate-50/95"
                )}
                aria-hidden
              >
                <LoadingSpinner tone={tone === "dark" ? "dark" : "light"} size="md" />
                <p className={cx("mt-3 text-xs font-medium", tone === "dark" ? "text-slate-400" : "text-slate-500")}>
                  Loading demo…
                </p>
              </div>
            )}
            <div
              className={cx(matchColumnHeight && "flex-1 min-h-0 flex flex-col")}
              style={{
                transform: iframeScale ? `scale(${iframeScale})` : undefined,
                transformOrigin: "top left",
                width: iframeScale ? `${100 / iframeScale}%` : "100%",
                height: iframeScale ? `${100 / iframeScale}%` : matchColumnHeight ? "100%" : undefined,
              }}
            >
              <iframe
                ref={iframeRef}
                src={iframeSrc}
                className={cx("w-full border-0", matchColumnHeight && "flex-1 min-h-[360px]")}
                style={matchColumnHeight ? undefined : { height: iframeHeight ?? 600 }}
                title={title}
                loading="lazy"
                allow="fullscreen; clipboard-read; clipboard-write"
                referrerPolicy="no-referrer-when-downgrade"
                onLoad={onIframeLoad}
                onError={() => { setIframeLoadFailed(true); setIframeLoading(false); }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DemoSlotToggle({
  options,
  value,
  onChange,
  tone,
}: {
  options: Array<{ value: DemoVariant; label: string }>;
  value: DemoVariant;
  onChange: (v: DemoVariant) => void;
  tone: Tone;
}) {
  const wrap = tone === "dark" ? "border-white/10 bg-white/5" : "border-slate-200 bg-slate-50";
  const idle = tone === "dark" ? "text-white/70 hover:bg-white/10" : "text-slate-600 hover:bg-white/60";
  const active = tone === "dark" ? "text-white bg-white/15" : "text-white bg-gradient-to-r from-blue-600 to-blue-400 shadow-md";
  return (
    <div className={cx("inline-flex rounded-2xl border p-1", wrap)}>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={cx(
            "px-3 py-1.5 text-xs font-semibold rounded-xl transition-all duration-300",
            value === opt.value ? active : idle
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function DemoShowcase({
  track,
  tone,
  onTrackChange,
  initialVariantMck,
  initialVariantBcg,
}: {
  track: Track;
  tone: Tone;
  onTrackChange?: (t: Track) => void;
  initialVariantMck?: DemoVariant;
  initialVariantBcg?: DemoVariant;
}) {
  const showSeaWolf = track === "all" || track === "mck";
  const showCasey = track === "all" || track === "bcg";
  const showExpand = track === "all" && onTrackChange;

  const [demoVariantMck, setDemoVariantMck] = useState<DemoVariant>(initialVariantMck ?? "assessment");
  const [demoVariantBcg, setDemoVariantBcg] = useState<DemoVariant>(initialVariantBcg ?? "assessment");

  const demosUnavailable = useState(() => !isDemosEnvironmentAvailable())[0];

  useEffect(() => {
    if (initialVariantMck != null) setDemoVariantMck(initialVariantMck);
    if (initialVariantBcg != null) setDemoVariantBcg(initialVariantBcg);
  }, [initialVariantMck, initialVariantBcg]);

  return (
    <div id="live-demos" className={cx(tone === "dark" ? "bg-slate-950" : "bg-white")}>
      <style>{`
        #live-demos .live-demos-section-title {
          background: linear-gradient(135deg, #0f172a 0%, #334155 25%, #0f172a 50%, #64748b 75%, #0f172a 100%);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          position: relative;
          display: inline-block;
        }
        #live-demos .live-demos-section-title::after {
          content: '';
          position: absolute;
          bottom: -8px;
          left: 50%;
          transform: translateX(-50%);
          width: 80px;
          height: 3px;
          background: linear-gradient(to right, transparent 0%, rgba(15,23,42,0.3) 25%, rgba(15,23,42,0.5) 50%, rgba(15,23,42,0.3) 75%, transparent 100%);
          border-radius: 2px;
        }
        #live-demos.bg-slate-950 .live-demos-section-title {
          background: linear-gradient(135deg, #fff 0%, #cbd5e1 25%, #fff 50%, #94a3b8 75%, #fff 100%);
          -webkit-background-clip: text;
          background-clip: text;
        }
        #live-demos.bg-slate-950 .live-demos-section-title::after {
          background: linear-gradient(to right, transparent 0%, rgba(255,255,255,0.3) 25%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0.3) 75%, transparent 100%);
        }
      `}</style>
      <div className="w-full max-w-[92.8%] mx-auto px-4 sm:px-8 md:px-16 py-8 sm:py-10 md:py-12 text-center">
        <div className="flex justify-center mb-3">
          <BoltIcon className={cx("w-[18px] h-[18px]", tone === "dark" ? "text-white/80" : "text-slate-600")} />
        </div>
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-8 sm:mb-10 md:mb-12 live-demos-section-title">
          Live demos
        </h2>
        <p className={cx("text-sm font-semibold mb-10", tone === "dark" ? "text-white/70" : "text-slate-600")}>
          {track === "all"
            ? "Preview assessments and AI coaches: switch with the buttons below each demo."
            : track === "mck"
            ? "Preview Sea Wolf, Red Rock, or McKinsey Lilli AI Interview Coach."
            : "Preview Casey or BCG Candidate-led Interview Coach."}
        </p>

        <div className={cx("grid gap-6 md:gap-8", track === "all" ? "lg:grid-cols-2" : "")}>
          {showSeaWolf ? (
            <div className={cx("flex flex-col gap-3", track === "all" && "min-h-0")}>
              <div className="flex flex-wrap justify-center">
                <DemoSlotToggle
                  tone={tone}
                  options={[
                    { value: "assessment", label: "McKinsey Sea Wolf" },
                    { value: "redrock", label: "Red Rock" },
                    { value: "aiCoach", label: "McKinsey Lilli AI Interview Coach" },
                  ]}
                  value={demoVariantMck}
                  onChange={setDemoVariantMck}
                />
              </div>
              {demoVariantMck === "assessment" ? (
                <DemoFrame
                  key="mck-assessment"
                  tone={tone}
                  title="Sea-Wolf Demo"
                  subtitle="McKinsey assessment practice"
                  iframeHint="Try the Sea-Wolf assessment demo."
                  iframeSrc={getEffectiveDemoIframeUrl("seaWolf", SEA_WOLF_DEMO_URL)}
                  iframeSourceTag="seawolf-demo"
                  bareIframe
                  iframeMaxHeight="58vh"
                  expandLabel={showExpand ? "View on McKinsey" : undefined}
                  onExpand={showExpand ? () => onTrackChange?.("mck") : undefined}
                  matchColumnHeight={track === "all"}
                  hideScrollbar={track === "all"}
                  demosUnavailable={demosUnavailable}
                />
              ) : demoVariantMck === "redrock" ? (
                <DemoFrame
                  key="mck-redrock"
                  tone={tone}
                  title="Red Rock Demo"
                  subtitle="McKinsey Red Rock assessment practice"
                  iframeHint="Try the Red Rock simulator demo."
                  iframeSrc={getEffectiveDemoIframeUrl("redrock", REDROCK_DEMO_URL)}
                  iframeSourceTag="redrock-demo"
                  bareIframe
                  iframeMaxHeight="58vh"
                  iframeMinHeight="50vh"
                  matchColumnHeight={track === "all"}
                  hideScrollbar={track === "all"}
                  demosUnavailable={demosUnavailable}
                  iframeLoadFallbackHint="To view this demo, start the Red Rock simulator: in redrock-simulation run npm run dev (port 8090). Then reload this page."
                  expandLabel={showExpand ? "View on McKinsey" : undefined}
                  onExpand={showExpand ? () => onTrackChange?.("mck") : undefined}
                />
              ) : (
                <DemoFrame
                  key="mck-aicoach"
                  tone={tone}
                  title="McKinsey Lilli AI Interview Coach"
                  subtitle="McKinsey AI case coach (placeholder)"
                  iframeHint="AI coach demo — coming soon."
                  iframeSrc={getEffectiveDemoIframeUrl("mckinseyCoach", `${MCKINSEY_AI_COACH_DEMO_URL}${MCKINSEY_AI_COACH_DEMO_URL.includes("?") ? "&" : "?"}demo=1`)}
                  iframeSourceTag="mckinsey-ai-coach-demo"
                  iframeMaxHeight="58vh"
                  bareIframe
                  matchColumnHeight={track === "all"}
                  demosUnavailable={demosUnavailable}
                  iframeLoadFallbackHint="To view this demo, start the coach app: in cognidoor-ai-case-coach run npm run dev:mckinsey:server (API) and npm run dev:mckinsey:vite (UI on port 5174). Then reload this page."
                  expandLabel={showExpand ? "View on McKinsey" : undefined}
                  onExpand={showExpand ? () => onTrackChange?.("mck") : undefined}
                />
              )}
            </div>
          ) : null}
          {showCasey ? (
            <div className={cx("flex flex-col gap-3", track === "all" && "min-h-0")}>
              <div className="flex flex-wrap justify-center">
                <DemoSlotToggle
                  tone={tone}
                  options={[
                    { value: "assessment", label: "BCG Casey" },
                    { value: "aiCoach", label: "BCG Candidate-led Interview Coach" },
                  ]}
                  value={demoVariantBcg}
                  onChange={setDemoVariantBcg}
                />
              </div>
              {demoVariantBcg === "assessment" ? (
                <DemoFrame
                  tone={tone}
                  title="BCG Casey Demo"
                  subtitle="BCG assessment practice"
                  iframeHint="Try the BCG Casey assessment demo."
                  iframeSrc={getEffectiveDemoIframeUrl("casey", CASEY_DEMO_URL)}
                  iframeSourceTag="casey-demo"
                  expandLabel={showExpand ? "View on BCG" : undefined}
                  onExpand={showExpand ? () => onTrackChange?.("bcg") : undefined}
                  matchColumnHeight={track === "all"}
                  demosUnavailable={demosUnavailable}
                />
              ) : (
                <DemoFrame
                  tone={tone}
                  title="BCG Candidate-led Interview Coach"
                  subtitle="BCG AI case coach (placeholder)"
                  iframeHint="AI coach demo — coming soon."
                  iframeSrc={getEffectiveDemoIframeUrl("bcgCoach", `${BCG_AI_COACH_DEMO_URL}${BCG_AI_COACH_DEMO_URL.includes("?") ? "&" : "?"}demo=1`)}
                  iframeSourceTag="bcg-ai-coach-demo"
                  iframeMaxHeight="58vh"
                  bareIframe
                  matchColumnHeight={track === "all"}
                  demosUnavailable={demosUnavailable}
                  iframeLoadFallbackHint="To view this demo, start the coach app: in cognidoor-ai-case-coach run npm run dev:mckinsey:server (API) and npm run dev:bcg:vite (UI on port 5176). Then reload this page."
                  expandLabel={showExpand ? "View on BCG" : undefined}
                  onExpand={showExpand ? () => onTrackChange?.("bcg") : undefined}
                />
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function PricingBlock({
  track,
  billing,
  onBillingChange,
  tone,
  onGetStarted,
  onTryDemo,
  currentPlanId = null,
  highlightPlanId = null,
}: {
  track: Track;
  billing: Billing;
  onBillingChange: (b: Billing) => void;
  tone: Tone;
  onGetStarted?: (plan: Plan, billing: Billing) => void;
  onTryDemo?: () => void;
  /** Logged-in user's active plan_id; used for "Your current plan" / "Upgrade" / dimming. */
  currentPlanId?: string | null;
  /** Plan card to highlight when navigating from product upgrade CTA. */
  highlightPlanId?: string | null;
}) {
  const plans = getRecommendedPlans(track);
  const isDark = tone === "dark";
  const hasActivePlan = currentPlanId != null && currentPlanId.length > 0;
  const isAllAccess = currentPlanId === "all_access";

  return (
    <section id="pricing" className={cx(isDark ? "bg-slate-950" : "border-t border-slate-200/50 bg-gradient-to-b from-white via-blue-50/30 to-white")}>
      <style>{`
        .pricing-section-title {
          background: linear-gradient(135deg, #0f172a 0%, #334155 25%, #0f172a 50%, #64748b 75%, #0f172a 100%);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          position: relative;
          display: inline-block;
        }
        .pricing-section-title::after {
          content: '';
          position: absolute;
          bottom: -8px;
          left: 50%;
          transform: translateX(-50%);
          width: 80px;
          height: 3px;
          background: linear-gradient(to right, transparent 0%, rgba(15,23,42,0.3) 25%, rgba(15,23,42,0.5) 50%, rgba(15,23,42,0.3) 75%, transparent 100%);
          border-radius: 2px;
        }
        section#pricing.bg-slate-950 .pricing-section-title {
          background: linear-gradient(135deg, #fff 0%, #cbd5e1 25%, #fff 50%, #94a3b8 75%, #fff 100%);
          -webkit-background-clip: text;
          background-clip: text;
        }
        section#pricing.bg-slate-950 .pricing-section-title::after {
          background: linear-gradient(to right, transparent 0%, rgba(255,255,255,0.3) 25%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0.3) 75%, transparent 100%);
        }
      `}</style>
      <div className="w-full max-w-[92.8%] mx-auto px-4 sm:px-8 md:px-16 py-12 sm:py-16 md:py-20 lg:py-24 text-center">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-8 sm:mb-10 md:mb-12 pricing-section-title">
          Pricing
        </h2>
        <p className={cx("text-sm font-semibold mb-6 sm:mb-8", isDark ? "text-white/70" : "text-slate-600")}>
          Recommended for your track.
        </p>

        <div className="flex flex-wrap justify-center gap-2 mb-8">
          <Segmented
            value={billing}
            onChange={(v) => onBillingChange(v as Billing)}
            items={[
              { value: "monthly", label: "Monthly" },
              { value: "sixMo", label: "6-month" },
            ]}
            tone={tone}
            indicatorId="segmented-billing"
            appearance="softPill"
          />
        </div>

        {/* Pricing cards: centered for McKinsey/BCG (3), full-width grid for All (4) */}
        <div
          className={cx(
            "mt-8 gap-4 sm:gap-6",
            track === "all"
              ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 max-w-6xl mx-auto"
              : "flex flex-wrap justify-center"
          )}
        >
          {plans.map((p) => {
            const isCurrentPlan = currentPlanId === p.plan_id;
            const isDimmed = isAllAccess && p.plan_id !== "all_access";
            const showUpgrade = hasActivePlan && !isCurrentPlan && !isDimmed;
            return (
              <div
                key={p.plan_id}
                className={cx(
                  "flex",
                  track === "all" ? "w-full min-w-0 min-h-[460px]" : "w-full min-w-[260px] max-w-[340px] sm:max-w-[320px]"
                )}
              >
                <PlanCard
                  plan={p}
                  billing={billing}
                  accent={tone}
                  onGetStarted={onGetStarted}
                  onTryDemo={onTryDemo}
                  tagOverride={
                    track === "all"
                      ? p.plan_id === "mckinsey_track_full" || p.plan_id === "bcg_track_full"
                        ? "Digital assessment & coach"
                        : p.plan_id === "all_access"
                          ? "Most popular"
                          : undefined
                    : undefined
                  }
                  isCurrentPlan={isCurrentPlan}
                  isDimmed={isDimmed}
                  showUpgrade={showUpgrade}
                  isHighlighted={highlightPlanId === p.plan_id}
                />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ---------- Signature visuals (V4) (V4) ----------

function DottedOrbit({ className, size = 520 }: { className?: string; size?: number }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 600 600"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <radialGradient
          id="orbGlow"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(300 300) rotate(90) scale(280)"
        >
          <stop stopColor="#38BDF8" stopOpacity="0.18" />
          <stop offset="0.5" stopColor="#34D399" stopOpacity="0.10" />
          <stop offset="1" stopColor="#6366F1" stopOpacity="0" />
        </radialGradient>
        <pattern id="dots" x="0" y="0" width="18" height="18" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="1" fill="#0F172A" fillOpacity="0.10" />
        </pattern>
      </defs>

      <circle cx="300" cy="300" r="280" fill="url(#orbGlow)" />
      <circle cx="300" cy="300" r="240" stroke="#0F172A" strokeOpacity="0.10" strokeWidth="2" />
      <circle cx="300" cy="300" r="170" stroke="#0F172A" strokeOpacity="0.08" strokeWidth="2" strokeDasharray="3 9" />
      <circle cx="300" cy="300" r="110" stroke="#0F172A" strokeOpacity="0.08" strokeWidth="2" strokeDasharray="2 10" />
      <circle cx="300" cy="300" r="245" fill="url(#dots)" fillOpacity="0.35" />

      {[
        { x: 300, y: 60, c: "#277AB2" },
        { x: 520, y: 300, c: "#0B5996" },
        { x: 300, y: 540, c: "#277AB2" },
        { x: 80, y: 300, c: "#0B5996" },
      ].map((n, i) => (
        <g key={i}>
          <circle cx={n.x} cy={n.y} r={10} fill={n.c} fillOpacity="0.20" />
          <circle cx={n.x} cy={n.y} r={4} fill={n.c} fillOpacity="0.65" />
        </g>
      ))}
    </svg>
  );
}

function SubtleNoise({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="100%"
      height="100%"
      preserveAspectRatio="none"
    >
      <filter id="n">
        <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="3" stitchTiles="stitch" />
        <feColorMatrix type="saturate" values="0" />
        <feComponentTransfer>
          <feFuncA type="table" tableValues="0 0.22" />
        </feComponentTransfer>
      </filter>
      <rect width="100%" height="100%" filter="url(#n)" opacity="0.22" />
    </svg>
  );
}

// ---------- Shared hero copy ----------

type HeroProofItem = { label?: string; value: string };

function heroContent(
  track: Track,
  opts?: { paidCustomer?: boolean }
): {
  eyebrowChips: string[];
  headline: string;
  subheadline: string;
  primaryCta: string;
  secondaryCta: string;
  proofRow: HeroProofItem[];
} {
  if (track === "mck") {
    return {
      eyebrowChips: ["McKinsey AI assessment and interview coach"],
      headline: "Practice smarter for McKinsey Solve and interview prep.",
      subheadline:
        "Train with our Sea Wolf and Red Rock simulations and AI interview coach built for McKinsey-style assessments and interviews. Learn the logic, sharpen your thinking, and build real confidence before the real thing.",
      primaryCta: "Start McKinsey demo",
      secondaryCta: "See how it works",
      proofRow: [
        { value: "SOLVE Assessment Prep" },
        { value: "Lilli Interview AI Coaching" },
        { value: "Built for McKinsey-style practice" },
      ],
    };
  }
  if (track === "bcg") {
    return {
      eyebrowChips: ["Candidate-led BCG interview coach"],
      headline: "Lead BCG case prep with more clarity and less guesswork.",
      subheadline:
        "Practise with Casey-style assessment practice and a candidate-led AI coach that helps you structure better, think more clearly, and improve with feedback that actually teaches you something.",
      primaryCta: "Start BCG demo",
      secondaryCta: "See how it works",
      proofRow: [
        { value: "Casey Assessment Prep" },
        { value: "Candidate-led AI Coaching" },
        { value: "Built for BCG-style interviews" },
      ],
    };
  }
  if (opts?.paidCustomer) {
    return {
      eyebrowChips: ["Fast demo", "See the answer and the why", "Interactive previews"],
      headline: "Practice smarter for McKinsey and BCG: assessments and interview prep in one place.",
      subheadline:
        "Interactive assessment simulations (Sea Wolf, Red Rock, Casey) and AI coaching that show you the answer, explain the logic, and help you improve from your first try to interview-ready.",
      primaryCta: "Explore live demos",
      secondaryCta: "View pricing",
      proofRow: [
        { value: "Pre-interview assessment simulations" },
        { value: "AI interview coach" },
        { value: "McKinsey and BCG assessment and interview coach" },
      ],
    };
  }
  return {
    eyebrowChips: ["Fast demo", "See the answer and the why", "Free preview"],
    headline: "Practice smarter for McKinsey and BCG: assessments and interview prep in one place.",
    subheadline:
      "Interactive assessment simulations (Sea Wolf, Red Rock, Casey) and AI coaching that show you the answer, explain the logic, and help you improve from your first try to interview-ready.",
    primaryCta: "Start free demo",
    secondaryCta: "View pricing",
    proofRow: [
      { value: "Pre-interview assessment simulations" },
      { value: "AI interview coach" },
      { value: "McKinsey and BCG assessment and interview coach" },
    ],
  };
}

/** Track-specific Result preview content: product label + Answer / Why / Next move (repo-grounded). */
function resultPreviewCopy(track: Track) {
  if (track === "mck") {
    return {
      productLabel: "From McKinsey Sea Wolf",
      answerTitle: "80%. Four of five checks passed.",
      answerSub: "Your trio hits the attribute ranges; one trait gap.",
      whyTitle: "Mobility and Energy in range.",
      whySub: "The swap fixes the remaining gap.",
      nextTitle: "Swap out one microbe → swap in a higher-Mobility option.",
      nextSub: "Raises the average into range.",
    };
  }
  if (track === "bcg") {
    return {
      productLabel: "From BCG Casey",
      answerTitle: "Prioritize Region B first.",
      answerSub: "Higher upside with fewer execution risks.",
      whyTitle: "Stronger unit economics support the move.",
      whySub: "The key risk is visible and manageable.",
      nextTitle: "Pressure-test capacity before rollout.",
      nextSub: "One final check, then move.",
    };
  }
  return {
    productLabel: "From AI case coach",
    answerTitle: "Recommendation is executive-ready.",
    answerSub: "You made the recommendation clear and supported.",
    whyTitle: "You anchored on the key number and named the main risk.",
    whySub: "Structure supports the conclusion.",
    nextTitle: "Focus your next run on the dimension your report flagged.",
    nextSub: "One targeted improvement, then move.",
  };
}

// ---------- Design V4 (Signature) ----------

function DesignV4({
  track,
  billing,
  onBillingChange,
  onTrackChange,
  onTrackSelect,
  onTryDemo,
  onViewPricing,
  onGetStarted,
  onSeeHowItWorks,
  onPromoNavigate,
  showTrackSelector,
  initialDemoVariantMck,
  initialDemoVariantBcg,
  currentPlanId,
  hideFreeTrialMarketing,
  highlightPlanId,
  children,
}: {
  track: Track;
  billing: Billing;
  onBillingChange: (b: Billing) => void;
  /** Called when "View full demo" is clicked — switch track and scroll to demos. */
  onTrackChange?: (t: Track) => void;
  /** Called when Segmented (All/McKinsey/BCG) is clicked — only switch track, no scroll. */
  onTrackSelect?: (t: Track) => void;
  onTryDemo?: () => void;
  onViewPricing?: () => void;
  /** Called when "Get started" is clicked on a plan card — navigate to checkout with plan and billing. */
  onGetStarted?: (plan: Plan, billing: Billing) => void;
  /** Called when "See how it works" is clicked (mck/bcg); scrolls to live demos. */
  onSeeHowItWorks?: () => void;
  onPromoNavigate?: (slug: PromoSlug) => void;
  showTrackSelector?: boolean;
  /** Initial demo tab when navigating to landing with scrollToLiveDemos (e.g. from product page). */
  initialDemoVariantMck?: DemoVariant;
  initialDemoVariantBcg?: DemoVariant;
  /** Logged-in user's active plan_id for pricing "Your current plan" / "Upgrade" / dimming. */
  currentPlanId?: string | null;
  /** Signed-in user with active paid plan: hide beta / free-trial marketing in the hero. */
  hideFreeTrialMarketing?: boolean;
  /** Plan card to highlight when navigating from product upgrade CTA. */
  highlightPlanId?: string | null;
  /** Rendered after Live demos and before Pricing (e.g. Why Cognidoor, Unlock powerful prep). */
  children?: React.ReactNode;
}) {
  const { user } = useAuthUser();
  const trackSelectorWrapperRef = useRef<HTMLDivElement>(null);
  const trackSelectorStopTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [promoStatusBySlug, setPromoStatusBySlug] = useState<Partial<Record<PromoSlug, PromoCampaignStatus>>>({});
  const hero = heroContent(track, { paidCustomer: Boolean(hideFreeTrialMarketing) && track === "all" });
  const preview = resultPreviewCopy(track);
  const handleSecondaryCta = track === "all" ? onViewPricing : onSeeHowItWorks;
  const mckPromo = promoStatusBySlug[MCK_PROMO_SLUG];
  const bcgPromo = promoStatusBySlug[BCG_PROMO_SLUG];

  const handleAnnouncementPricingCta = () => {
    onViewPricing?.();
    const scrollWithRetry = (attempt = 0) => {
      const el = document.getElementById("pricing");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        if (window.location.hash !== "#pricing") {
          window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#pricing`);
        }
        return;
      }
      if (attempt < 4) {
        window.setTimeout(() => scrollWithRetry(attempt + 1), 120);
      }
    };
    scrollWithRetry();
  };

  const hasOpenSeats = (status?: PromoCampaignStatus) => {
    if (!status) return true;
    return status.status === "active" && Number(status.remaining_claims || 0) > 0;
  };
  const showMckPromoCta = !hideFreeTrialMarketing && hasOpenSeats(mckPromo);
  const showBcgPromoCta = !hideFreeTrialMarketing && hasOpenSeats(bcgPromo);

  useEffect(() => {
    if (!showTrackSelector || !onTrackSelect || !trackSelectorWrapperRef.current) return;
    const el = trackSelectorWrapperRef.current;

    const startAnimationAndStopAfter10s = () => {
      el.classList.remove("track-selector-stopped");
      if (trackSelectorStopTimeoutRef.current) clearTimeout(trackSelectorStopTimeoutRef.current);
      trackSelectorStopTimeoutRef.current = setTimeout(() => {
        el.classList.add("track-selector-stopped");
        trackSelectorStopTimeoutRef.current = null;
      }, 10000);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) startAnimationAndStopAfter10s();
      },
      { threshold: 0.2, rootMargin: "0px" }
    );
    observer.observe(el);
    startAnimationAndStopAfter10s();

    return () => {
      observer.disconnect();
      if (trackSelectorStopTimeoutRef.current) clearTimeout(trackSelectorStopTimeoutRef.current);
    };
  }, [showTrackSelector, onTrackSelect]);

  useEffect(() => {
    let cancelled = false;
    let pollTimer: ReturnType<typeof setInterval> | null = null;

    async function loadPromoStatus() {
      if (hideFreeTrialMarketing || !user || !auth.currentUser) return;
      try {
        const token = await auth.currentUser.getIdToken();
        const slugs: PromoSlug[] = [MCK_PROMO_SLUG, BCG_PROMO_SLUG];
        const responses = await Promise.all(
          slugs.map((slug) =>
            fetch(`${API_BASE}/api/promo/campaigns/${encodeURIComponent(slug)}/status`, {
              headers: { Authorization: `Bearer ${token}` },
            }).then(async (res) => {
              const payload = await res.json().catch(() => null);
              return { ok: res.ok, slug, payload };
            })
          )
        );
        if (cancelled) return;
        const nextState: Partial<Record<PromoSlug, PromoCampaignStatus>> = {};
        for (const result of responses) {
          if (result.ok && result.payload && typeof result.payload === "object") {
            nextState[result.slug] = {
              status: typeof result.payload.status === "string" ? result.payload.status : undefined,
              remaining_claims: Number.isFinite(Number(result.payload.remaining_claims))
                ? Number(result.payload.remaining_claims)
                : undefined,
            };
          }
        }
        setPromoStatusBySlug(nextState);
      } catch {
        if (!cancelled) {
          setPromoStatusBySlug({});
        }
      }
    }

    if (hideFreeTrialMarketing) {
      setPromoStatusBySlug({});
      return () => {
        cancelled = true;
      };
    }
    void loadPromoStatus();
    if (user) {
      pollTimer = setInterval(() => {
        void loadPromoStatus();
      }, PROMO_POLL_MS);
    }
    return () => {
      cancelled = true;
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [user, hideFreeTrialMarketing]);
  const isAllTrack = track === "all";
  const previewShellClass = cx(
    "relative overflow-hidden border-white/20 ring-1 ring-white/10 shadow-[0_22px_60px_-26px_rgba(2,6,23,0.68)]",
    isAllTrack && "bg-white/16"
  );
  const previewCardClass = cx(
    "rounded-3xl border border-white/15 p-5 backdrop-blur-sm ring-1 ring-white/5 shadow-[0_16px_36px_-24px_rgba(2,6,23,0.78)]",
    isAllTrack
      ? "bg-[linear-gradient(180deg,rgba(255,255,255,0.18)_0%,rgba(255,255,255,0.12)_100%)]"
      : "bg-white/10"
  );
  const previewFooterClass = cx(
    "mt-6 rounded-3xl border border-white/15 p-5 backdrop-blur-sm ring-1 ring-white/5 shadow-[0_18px_38px_-24px_rgba(2,6,23,0.8)]",
    isAllTrack
      ? "bg-[linear-gradient(180deg,rgba(255,255,255,0.17)_0%,rgba(255,255,255,0.11)_100%)]"
      : "bg-white/10"
  );

  const heroBg =
    track === "mck"
      ? "bg-[linear-gradient(135deg,var(--cog-blue-1)_0%,var(--cog-blue-2)_55%,var(--cog-blue-3)_100%)]"
      : track === "bcg"
      ? "bg-[linear-gradient(135deg,var(--cog-blue-1)_0%,#10b981_40%,var(--cog-blue-2)_100%)]"
      : "bg-[linear-gradient(135deg,var(--cog-blue-1)_0%,#0891b2_30%,#34d399_60%,var(--cog-blue-2)_100%)]";

  return (
    <div className="bg-white text-slate-900">
      <style>{`
        .hero-subheadline-shimmer {
          position: relative;
          overflow: hidden;
        }
        .hero-subheadline-shimmer::after {
          content: '';
          position: absolute;
          width: 50%;
          height: 200%;
          top: -50%;
          left: -50%;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255, 255, 255, 0.2) 50%,
            transparent 100%
          );
          transform: rotate(-35deg);
          animation: hero-shine-diagonal 5s ease-in-out infinite;
          pointer-events: none;
          z-index: 1;
        }
        @keyframes hero-shine-diagonal {
          0% { transform: rotate(-35deg) translate(-100%, -100%); }
          18% { transform: rotate(-35deg) translate(250%, 250%); }
          100% { transform: rotate(-35deg) translate(250%, 250%); }
        }
        .promo-cta-shine {
          position: relative;
          overflow: hidden;
        }
        .promo-cta-shine::after {
          content: "";
          position: absolute;
          top: -120%;
          left: -60%;
          width: 45%;
          height: 320%;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.55) 50%, transparent 100%);
          transform: rotate(22deg);
          animation: promo-cta-sheen 3.8s ease-in-out infinite;
          pointer-events: none;
        }
        @keyframes promo-cta-sheen {
          0% { left: -60%; }
          35% { left: 130%; }
          100% { left: 130%; }
        }
      `}</style>
      <section className="relative overflow-hidden">
        <div className={cx("absolute inset-0 opacity-95", heroBg)} />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(255,255,255,0.25),transparent_45%),radial-gradient(circle_at_80%_20%,rgba(255,255,255,0.18),transparent_45%),radial-gradient(circle_at_50%_95%,rgba(255,255,255,0.10),transparent_45%)]" />
        <div className="pointer-events-none absolute inset-0 opacity-[0.10] [background-image:radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] [background-size:22px_22px]" />
        <div className="pointer-events-none absolute inset-0 opacity-[0.20]">
          <SubtleNoise className="h-full w-full" />
        </div>
        <motion.div
          aria-hidden
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="pointer-events-none absolute -right-56 top-10 hidden md:block"
        >
          <DottedOrbit className="opacity-[0.85]" />
        </motion.div>

        <div className="relative w-full max-w-[99%] ml-[1%] mr-0 px-4 sm:px-8 md:px-16 pt-12 sm:pt-16 md:pt-20 lg:pt-24 pb-20 sm:pb-28 md:pb-36 lg:pb-40">
          <div className="grid items-stretch gap-8 sm:gap-12 md:grid-cols-2 md:gap-16 lg:gap-24">
            <div className="text-white min-h-[280px] sm:min-h-[320px] md:min-h-[420px] h-full flex flex-col justify-center">
              <div className="flex flex-wrap items-center gap-2">
                {hero.eyebrowChips.length === 3 ? (
                  <>
                    <span className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold">
                      <ClockIcon className="w-3.5 h-3.5 mr-2" /> {hero.eyebrowChips[0]}
                    </span>
                    <span className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold">
                      <ShieldCheckIcon className="w-3.5 h-3.5 mr-2" /> {hero.eyebrowChips[1]}
                    </span>
                    <span className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold">
                      <LockClosedIcon className="w-3.5 h-3.5 mr-2" /> {hero.eyebrowChips[2]}
                    </span>
                  </>
                ) : (
                  <span className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold">
                    {hero.eyebrowChips[0]}
                  </span>
                )}
              </div>

              <LaunchAnnouncementBanner
                track={track}
                badge="New this week"
                title="Red Rock and Casey just got a serious upgrade."
                body="Three new ecological Red Rock cases are live, Red Rock Coach now gives clearer mistake diagnosis, and the new BCG Casey experience is cleaner, sharper, and better at teaching how to think through the case."
                primaryCta="Try the new practice"
                secondaryCta="See plans"
                onPrimaryCta={onTryDemo}
                onSecondaryCta={handleAnnouncementPricingCta}
                className="mt-5"
              />

              <h1 className="mt-8 text-balance text-4xl font-black leading-[1.05] tracking-tight md:text-6xl">
                {hero.headline}
              </h1>
              <div className="hero-subheadline-shimmer mt-7 max-w-prose relative overflow-hidden">
                <p className="relative z-0 max-w-prose text-pretty text-base font-bold leading-relaxed text-white/95 md:text-xl md:leading-relaxed">
                  {hero.subheadline}
                </p>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-3">
                <Button onClick={onTryDemo}>
                  {hero.primaryCta} <PlayIcon className="w-4 h-4" />
                </Button>
                <Button variant="dark" onClick={handleSecondaryCta}>{hero.secondaryCta}</Button>
                <span className="text-xs font-semibold text-white/70">
                  <CheckBadgeIcon className="w-3.5 h-3.5 mr-1 inline" /> Cognidoor blue signature
                </span>
              </div>

              <div className="mt-8 sm:mt-12 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {hero.proofRow.map((item, i) => (
                  <Glass key={i} className="flex min-h-[72px] min-w-0 flex-col items-center justify-center p-4 sm:p-5 text-center sm:min-h-[92px]">
                    {item.label ? (
                      <div className="text-xs font-bold text-white/70">{item.label}</div>
                    ) : null}
                    <div className={cx(item.label ? "mt-2 text-lg font-extrabold" : "text-lg font-extrabold", "min-w-0 leading-snug")}>
                      {item.value}
                    </div>
                  </Glass>
                ))}
              </div>

              {/* Promo hero CTA (limited-time/free seat messaging) temporarily hidden, keep code for future reuse. */}
              {false && (showMckPromoCta || showBcgPromoCta) ? (
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {showMckPromoCta ? (
                    <button
                      type="button"
                      onClick={() => onPromoNavigate?.(MCK_PROMO_SLUG)}
                      className="promo-cta-shine inline-flex min-h-[72px] items-center justify-center rounded-2xl bg-gradient-to-r from-orange-600 via-amber-500 to-orange-400 px-5 py-4 text-center text-base font-extrabold text-white shadow-lg transition-all duration-300 hover:scale-[1.03] hover:shadow-2xl active:scale-[0.99] sm:text-lg"
                    >
                      <span className="leading-tight">
                        Limited time only, claim your{" "}
                        <span className="text-xl sm:text-2xl">FREE</span>{" "}
                        McKinsey Solve simulations Pass
                        {typeof mckPromo?.remaining_claims === "number" ? ` (${mckPromo?.remaining_claims} seats left)` : ""}
                      </span>
                    </button>
                  ) : null}
                  {showBcgPromoCta ? (
                    <button
                      type="button"
                      onClick={() => onPromoNavigate?.(BCG_PROMO_SLUG)}
                      className="promo-cta-shine inline-flex min-h-[72px] items-center justify-center rounded-2xl bg-gradient-to-r from-orange-600 via-amber-500 to-orange-400 px-5 py-4 text-center text-base font-extrabold text-white shadow-lg transition-all duration-300 hover:scale-[1.03] hover:shadow-2xl active:scale-[0.99] sm:text-lg"
                    >
                      <span className="leading-tight">
                        Limited time only, claim your{" "}
                        <span className="text-xl sm:text-2xl">FREE</span>{" "}
                        BCG Casey simulation
                        {typeof bcgPromo?.remaining_claims === "number" ? ` (${bcgPromo?.remaining_claims} seats left)` : ""}
                      </span>
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="flex flex-col items-stretch min-h-[280px] sm:min-h-[320px] md:min-h-[420px] h-full justify-center">
              {showTrackSelector && onTrackSelect && (
                <div className="mb-3 w-full rounded-2xl border border-white/20 bg-white/50 backdrop-blur-sm p-1.5 shadow-xl ring-1 ring-black/5 hidden sm:block">
                  <div ref={trackSelectorWrapperRef} className="relative inline-flex">
                    <svg className="absolute inset-0 w-full h-full pointer-events-none overflow-visible" viewBox="0 0 220 50" preserveAspectRatio="none" aria-hidden>
                      <defs>
                        <linearGradient id="track-selector-flash-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#0B5A96" />
                          <stop offset="45%" stopColor="#38bdf8" />
                          <stop offset="55%" stopColor="#7dd3fc" />
                          <stop offset="100%" stopColor="#0B5A96" />
                        </linearGradient>
                        <filter id="track-selector-flash-glow" x="-20%" y="-20%" width="140%" height="140%">
                          <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="blur" />
                          <feColorMatrix in="blur" type="matrix" values="0 0 0 0 0.2  0 0 0 0 0.5  0 0 0 0 1  0 0 0 0.9 0" result="glow" />
                          <feMerge>
                            <feMergeNode in="glow" />
                            <feMergeNode in="SourceGraphic" />
                          </feMerge>
                        </filter>
                      </defs>
                      <path
                        className="track-selector-border-path"
                        d="M 14,1 L 207,1 Q 219,1 219,14 L 219,36 Q 219,49 206,49 L 14,49 Q 1,49 1,36 L 1,14 Q 1,1 14,1"
                        fill="none"
                        stroke="url(#track-selector-flash-gradient)"
                        strokeWidth="6"
                        strokeLinecap="round"
                        pathLength="1"
                        strokeDasharray="0.22 0.78"
                        filter="url(#track-selector-flash-glow)"
                      />
                    </svg>
                    <Segmented
                      value={track}
                      onChange={(v) => onTrackSelect(v as Track)}
                      items={[
                        { value: "all", label: "All" },
                        { value: "mck", label: "McKinsey" },
                        { value: "bcg", label: "BCG" },
                      ]}
                      tone="light"
                      indicatorId="segmented-track-hero"
                      className="track-selector-attention"
                    />
                  </div>
                </div>
              )}
              <Glass className={previewShellClass}>
              <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(2,6,23,0.06)_0%,rgba(2,6,23,0.16)_100%)]" />
              <div className="pointer-events-none absolute -right-16 top-8 h-36 w-36 rounded-full bg-white/10 blur-3xl opacity-60" />
              <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/60 to-transparent" />
              <div className="relative border-b border-white/15 p-6 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="grid h-11 w-11 place-items-center rounded-2xl border border-white/10 bg-white/12 shadow-[0_12px_28px_-18px_rgba(2,6,23,0.9)]">
                      <ChartBarIcon className="w-[18px] h-[18px]" />
                    </div>
                    <div>
                      <div className="text-sm font-extrabold">Result preview</div>
                      <div className="mt-1 text-xs font-semibold text-white/70">Answer • Why it works • Next move</div>
                      <div className="mt-1.5 inline-flex items-center rounded-xl border border-white/20 bg-white/18 px-2.5 py-1 text-[11px] font-semibold text-white shadow-[0_12px_28px_-18px_rgba(2,6,23,0.95)] backdrop-blur-md ring-1 ring-white/10">
                        {preview.productLabel}
                      </div>
                    </div>
                  </div>
                  <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-bold">Preview</span>
                </div>
              </div>
              <div className="relative p-6 text-white">
                <div className="grid gap-4">
                  <div className={previewCardClass}>
                    <div className="text-xs font-bold text-white/70">Answer</div>
                    <div className="mt-2 text-sm font-extrabold text-white">{preview.answerTitle}</div>
                    <div className="mt-1 text-sm text-white/80">{preview.answerSub}</div>
                  </div>
                  <div className={previewCardClass}>
                    <div className="text-xs font-bold text-white/70">Why it works</div>
                    <div className="mt-2 text-sm font-extrabold text-white">{preview.whyTitle}</div>
                    <div className="mt-1 text-sm text-white/80">{preview.whySub}</div>
                  </div>
                  <div className={previewCardClass}>
                    <div className="text-xs font-bold text-white/70">Next move</div>
                    <div className="mt-2 text-sm font-extrabold text-white">{preview.nextTitle}</div>
                    <div className="mt-1 text-sm text-white/80">{preview.nextSub}</div>
                  </div>
                </div>
                <div className={previewFooterClass}>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-extrabold">Try it first. Pay only if it clicks.</div>
                      <div className="mt-1 text-xs font-semibold text-white/70">See the answer, the reasoning, and the next move in under 90s.</div>
                    </div>
                    <Button onClick={onTryDemo}>
                      Try demo <BoltIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              </Glass>
            </div>
          </div>
        </div>

        <div className="relative h-16 bg-white">
          <div className="absolute -top-14 left-0 right-0 h-20 bg-white [clip-path:polygon(0%_65%,100%_0%,100%_100%,0%_100%)]" />
        </div>
      </section>

      <DemoShowcase track={track} tone="light" onTrackChange={onTrackChange} initialVariantMck={initialDemoVariantMck} initialVariantBcg={initialDemoVariantBcg} />

      {children}

      <PricingBlock
        track={track}
        billing={billing}
        onBillingChange={onBillingChange}
        tone="light"
        onGetStarted={onGetStarted ?? onViewPricing}
        onTryDemo={onTryDemo}
        currentPlanId={currentPlanId}
        highlightPlanId={highlightPlanId}
      />
      <FaqSection />
    </div>
  );
}

// ---------- Compare Harness ---------- ----------

const LANDING_SCROLL_THRESHOLD = 200;

/** Location state for navigating to landing with a specific section and/or demo selected. */
export type LandingNavState = {
  scrollToPricing?: boolean;
  scrollToLiveDemos?: boolean;
  highlight?: string;
  track?: Track;
  demoVariantMck?: DemoVariant;
  demoVariantBcg?: DemoVariant;
};

export default function CognidoorLandingCompare({ children }: { children?: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const ctx = useTrack();
  const track = ctx.track as Track;
  const setTrack = ctx.setTrack as (t: Track) => void;
  const { user } = useAuthUser();
  const { planId: subscriptionPlanId, isSubscriptionActive, loading: subscriptionLoading } = useSubscription();
  const hideFreeTrialMarketing =
    Boolean(user) && !subscriptionLoading && isSubscriptionActive && subscriptionPlanId != null;
  const currentPlanId = isSubscriptionActive && subscriptionPlanId ? subscriptionPlanId : null;
  const [billing, setBilling] = useState<Billing>("monthly");
  const [scrollY, setScrollY] = useState(0);
  const state = (location.state ?? {}) as LandingNavState;
  const params = new URLSearchParams(location.search);
  const highlightPlanId = params.get("highlight") || state.highlight || null;

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Apply track when navigating from product pages to pricing or live demos
  useEffect(() => {
    if ((state.scrollToPricing || state.scrollToLiveDemos) && state.track && ["all", "mck", "bcg"].includes(state.track)) {
      setTrack(state.track);
    }
  }, [state.scrollToPricing, state.scrollToLiveDemos, state.track, setTrack]);

  // Apply track and scroll to live demos when navigating from product pages (e.g. "Start Casey demo")
  useEffect(() => {
    if (state.scrollToLiveDemos) {
      const t = setTimeout(() => {
        const el = document.getElementById("live-demos");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
      return () => clearTimeout(t);
    }
  }, [state.scrollToLiveDemos]);

  const scrollToPricing = () => {
    const el = document.getElementById("pricing");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollToLiveDemos = () => {
    const el = document.getElementById("live-demos");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleTrackChange = (t: Track) => {
    setTrack(t);
    requestAnimationFrame(() => {
      const el = document.getElementById("live-demos");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const handleTryDemo = () => {
    scrollToLiveDemos();
  };

  const handleViewPricing = () => {
    scrollToPricing();
  };

  const handleGetStarted = (plan: Plan, billing: Billing) => {
    const billing_term = BILLING_UI_TO_PAYFAST[billing];
    const amount_usd = billing === "monthly" ? plan.monthly : plan.sixMo;
    navigate("/checkout", { state: { plan_id: plan.plan_id, billing_term, display_name: plan.name, amount_usd } });
  };
  const handlePromoNavigate = (slug: PromoSlug) => {
    navigate(`/promo/${slug}`);
  };

  const showTrackAboveHero = scrollY < LANDING_SCROLL_THRESHOLD;

  return (
    <div
      className="min-h-screen bg-slate-100"
      style={
        {
          "--cog-blue-1": "#277AB2",
          "--cog-blue-2": "#0B5996",
          "--cog-blue-3": "#082B4A",
        } as React.CSSProperties & { "--cog-blue-1"?: string; "--cog-blue-2"?: string; "--cog-blue-3"?: string }
      }
    >
      <div className="bg-white">
        <DesignV4 track={track} billing={billing} onBillingChange={setBilling} onTrackChange={handleTrackChange} onTrackSelect={setTrack} onTryDemo={handleTryDemo} onViewPricing={handleViewPricing} onGetStarted={handleGetStarted} onSeeHowItWorks={scrollToLiveDemos} onPromoNavigate={handlePromoNavigate} showTrackSelector={showTrackAboveHero} initialDemoVariantMck={state.demoVariantMck} initialDemoVariantBcg={state.demoVariantBcg} currentPlanId={currentPlanId} hideFreeTrialMarketing={hideFreeTrialMarketing} highlightPlanId={highlightPlanId}>
          {children}
        </DesignV4>
      </div>

    </div>
  );
}
